/**
 * @lodex-pro/web-admin/lib/use-profile — current admin user's profile + role.
 *
 * Phase 25 / Task 25.1 (DEC-111). Reads GET /v1/users/me/profile (any authed user;
 * no permission gate) and exposes the role so the app can gate the whole console on
 * PLATFORM_ADMIN. The server is authoritative — every admin endpoint is
 * permission-gated — so this gate is a UX convenience (hide admin chrome from a
 * non-admin), not a trust boundary.
 */
import { useApiOpts, useApiQuery } from '@lodex-pro/web-shared';
import type { UserProfile } from '@lodex-pro/shared';

export const USER_PROFILE_QUERY_KEY = ['users', 'me', 'profile'] as const;

interface UserProfileResponse {
  readonly profile: UserProfile;
}

/**
 * TanStack Query result for GET /v1/users/me/profile.
 *
 * `enabled` MUST be gated on authentication: firing pre-auth caches a 401 error
 * (staleTime Infinity → never refetched), which would wrongly deny a real admin
 * after they sign in. Pass `isAuthenticated && !loading`.
 */
export function useAdminProfile(
  enabled: boolean,
): ReturnType<typeof useApiQuery<UserProfileResponse>> {
  const opts = useApiOpts();
  return useApiQuery<UserProfileResponse>(opts, '/v1/users/me/profile', {
    queryKey: USER_PROFILE_QUERY_KEY,
    enabled,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });
}
